import { useState, useEffect } from "react";
import { randomEvents } from "../../../mocks/dashboard";
import { EVENT_TODOS, type Todo } from "../../../hooks/useTodos";
import { supabase } from "../../../lib/supabase";

interface Props {
  onClose: () => void;
  onAction: (reputationDelta: number, capitalDelta: number, chosenActionLabel?: string) => void;
  eventIndex?: number;
  onOpenNPC?: (npcId: string, context: string) => void;
  onAddTodos?: (todos: Omit<Todo, "id" | "done">[]) => void;
  onDefer?: (eventIdx: number) => void;
}

type Phase = "choice" | "processing" | "result" | "team_discuss";

// 이벤트 타입에 따른 협의 대상 팀원 목록
const DISCUSS_ROLES: Record<string, { role: string; label: string; icon: string; reason: string }[]> = {
  crisis: [
    { role: "Legal", label: "법무팀", icon: "ri-scales-line", reason: "법적 리스크 검토" },
    { role: "Marketing", label: "마케팅팀", icon: "ri-megaphone-line", reason: "대외 커뮤니케이션 전략" },
    { role: "Finance", label: "재무팀", icon: "ri-money-dollar-circle-line", reason: "손실 규모 추산" },
  ],
  opportunity: [
    { role: "Finance", label: "재무팀", icon: "ri-money-dollar-circle-line", reason: "수익성 분석" },
    { role: "AR", label: "A&R팀", icon: "ri-music-line", reason: "아티스트 일정 검토" },
    { role: "Marketing", label: "마케팅팀", icon: "ri-megaphone-line", reason: "마케팅 전략 수립" },
  ],
};

function getEventNPC(event: (typeof randomEvents)[0]): { id: string; name: string; tag: string; color: string } {
  const text = `${event.headline} ${event.description}`;
  if (text.includes("법적") || text.includes("표절") || text.includes("세무") || text.includes("내용증명") || text.includes("조사"))
    return { id: "legal", name: "장현주 변호사", tag: "법무 자문", color: "#0D9488" };
  if (text.includes("경쟁") || text.includes("협상") || text.includes("경쟁사"))
    return { id: "rival", name: "최현우 대표", tag: "경쟁사 협상", color: "#DC2626" };
  if (text.includes("팬") || text.includes("팬덤"))
    return { id: "fan", name: "이수현 회장", tag: "팬덤 소통", color: "#E8375F" };
  if (text.includes("투자") || text.includes("벤처") || text.includes("지분"))
    return { id: "investor", name: "박서준 대표", tag: "투자 협상", color: "#059669" };
  if (text.includes("언론") || text.includes("방송") || text.includes("다큐") || text.includes("미디어"))
    return { id: "journalist", name: "김민준 편집장", tag: "언론 대응", color: "#EA580C" };
  if (event.type === "opportunity")
    return { id: "investor", name: "박서준 대표", tag: "기회 전략", color: "#059669" };
  return { id: "mentor", name: "이지현 멘토", tag: "위기 대응", color: "#D97706" };
}

export default function RandomEventModal({ onClose, onAction, eventIndex: propIndex, onOpenNPC, onAddTodos, onDefer }: Props) {
  const [eventIndex] = useState(() =>
    propIndex !== undefined ? propIndex : Math.floor(Math.random() * randomEvents.length)
  );
  const [phase, setPhase] = useState<Phase>("choice");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [paused, setPaused] = useState(false);
  const [selectedDiscussRoles, setSelectedDiscussRoles] = useState<string[]>([]);
  const [discussSent, setDiscussSent] = useState(false);
  const [discussNote, setDiscussNote] = useState("");

  const event = randomEvents[eventIndex];
  const isCrisis = event.type === "crisis";
  const npcInfo = getEventNPC(event);
  const discussTargets = DISCUSS_ROLES[event.type] ?? DISCUSS_ROLES["crisis"];

  const handleAction = (i: number) => {
    if (phase !== "choice") return;
    setSelectedIdx(i);
    setPhase("processing");
    setTimeout(() => {
      setPhase("result");
      const eventTodos = EVENT_TODOS[event.id]?.[i];
      if (eventTodos && onAddTodos) {
        onAddTodos(eventTodos.map((t) => ({ ...t, source: "event" as const })));
      }
    }, 800);
  };

  // countdown ticker in result phase
  useEffect(() => {
    if (phase !== "result" || paused) return;
    setCountdown(5);
    const tick = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 0.1) {
          clearInterval(tick);
          if (selectedIdx !== null) {
            const action = event.actions[selectedIdx];
            onAction(action.result.reputation, action.result.capital, action.label);
          }
          onClose();
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused]);

  const selectedAction = selectedIdx !== null ? event.actions[selectedIdx] : null;
  const repDelta = selectedAction?.result.reputation ?? 0;
  const capDelta = selectedAction?.result.capital ?? 0;

  const handleOpenNPC = () => {
    setPaused(true);
    if (selectedIdx !== null) {
      const action = event.actions[selectedIdx];
      onAction(action.result.reputation, action.result.capital, action.label);
    }
    const context = `방금 발생한 이벤트: "${event.headline}". 상황: ${event.description}. 선택한 대응: ${selectedIdx !== null ? event.actions[selectedIdx].label : "미선택"}. 결과: 직급점수 ${repDelta > 0 ? "+" : ""}${repDelta}pt, 자본금 ${capDelta > 0 ? "+" : ""}${capDelta.toLocaleString()}만원. 이 상황에 맞는 전문적인 조언이나 협상 전략을 알려주세요.`;
    onOpenNPC?.(npcInfo.id, context);
    onClose();
  };

  // 팀 협의 요청 메일 발송 + 이벤트 defer
  const handleSendDiscuss = async () => {
    if (selectedDiscussRoles.length === 0) return;
    const wsId = localStorage.getItem("kap_solo_workspace_id");

    const actionList = event.actions.map((a, i) => `${String.fromCharCode(65+i)}. ${a.label}`).join("\n");
    const body = `안녕하세요.\n\n긴급 상황이 발생하여 팀의 의견이 필요합니다.\n\n[상황]\n${event.headline}\n\n${event.description}\n\n[현재 검토 중인 대응 옵션]\n${actionList}\n\n${discussNote ? `[추가 메모]\n${discussNote}\n\n` : ""}최대한 빠르게 의견 공유 부탁드립니다.\n\n감사합니다.`;

    // Supabase 메일 발송 (workspace가 있는 경우에만)
    if (wsId) {
      for (const role of selectedDiscussRoles) {
        await supabase.from("collab_messages").insert({
          workspace_id: wsId,
          sender_user_id: "00000000-0000-0000-0000-000000000000",
          sender_role: "CEO",
          receiver_role: role,
          task_id: `event_discuss_${event.id}`,
          task_title: `[팀 협의 요청] ${event.headline}`,
          subject: `[긴급 협의 요청] ${event.headline}`,
          body,
          is_read: false,
        });
      }
    }

    // localStorage에 미결 이벤트 저장 (항상 실행)
    const pending = JSON.parse(localStorage.getItem("kap_pending_events") ?? "[]");
    const exists = pending.find((p: { id: string }) => p.id === event.id);
    if (!exists) {
      pending.unshift({
        id: event.id,
        eventIndex,
        headline: event.headline,
        type: event.type,
        savedAt: new Date().toISOString(),
        discussRoles: selectedDiscussRoles,
      });
      localStorage.setItem("kap_pending_events", JSON.stringify(pending));
    }

    setDiscussSent(true);
    onDefer?.(eventIndex);
    setTimeout(() => onClose(), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-black/[0.08] rounded-2xl overflow-hidden shadow-xl">

        {/* Header */}
        <div className={`px-6 py-5 border-b border-black/[0.07] ${
          isCrisis ? "bg-gradient-to-r from-red-50 to-white" : "bg-gradient-to-r from-[#4B6CF7]/[0.05] to-white"
        }`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
              isCrisis ? "text-red-500 border-red-200 bg-red-50" : "text-[#4B6CF7] border-[#4B6CF7]/20 bg-[#4B6CF7]/[0.06]"
            }`}>
              {phase === "team_discuss" ? "👥 팀 협의 요청" : isCrisis ? "⚡ 위기 발생" : "✦ 기회 포착"}
            </span>
            {(phase === "choice" || phase === "team_discuss") && (
              <button type="button" onClick={onClose} className="text-[#CCCCCC] hover:text-[#888888] cursor-pointer transition-colors">
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>
          <h2 className="text-[#111111] font-semibold text-base leading-snug">{event.headline}</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5">

          {/* ── PHASE: choice ── */}
          {phase === "choice" && (
            <>
              <p className="text-[#666666] text-sm leading-relaxed mb-4">{event.description}</p>

              {/* 예상 영향 지표 */}
              <div className="flex gap-2 mb-5">
                <div className="flex-1 bg-[#F9F9F9] border border-black/[0.08] rounded-lg px-3 py-2.5 text-center">
                  <div className={`font-semibold text-sm tabular-nums ${event.impact.reputation < 0 ? "text-red-400" : "text-[#4B6CF7]"}`}>
                    {event.impact.reputation > 0 ? "+" : ""}{event.impact.reputation}pt
                  </div>
                  <div className="text-[#BBBBBB] text-[10px] mt-0.5">예상 직급점수</div>
                </div>
                <div className="flex-1 bg-[#F9F9F9] border border-black/[0.08] rounded-lg px-3 py-2.5 text-center">
                  <div className={`font-semibold text-sm tabular-nums ${event.impact.capital < 0 ? "text-red-400" : "text-emerald-500"}`}>
                    {event.impact.capital > 0 ? "+" : ""}{event.impact.capital.toLocaleString()}만
                  </div>
                  <div className="text-[#BBBBBB] text-[10px] mt-0.5">예상 자본</div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-[#CCCCCC] text-[10px] uppercase tracking-widest mb-2">대응 전략 선택</p>
                <div className="space-y-2">
                  {event.actions.map((action, i) => {
                    const repVal = action.result.reputation;
                    const capVal = action.result.capital;
                    const repPos = repVal >= 0;
                    const capPos = capVal >= 0;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleAction(i)}
                        className="w-full text-left rounded-xl border transition-all cursor-pointer group overflow-hidden border-black/[0.08] hover:border-[#4B6CF7]/30 hover:bg-[#4B6CF7]/[0.03]"
                      >
                        {/* 선택지 라벨 영역 */}
                        <div className="flex items-start gap-3 px-4 pt-3.5 pb-2">
                          <span className="w-5 h-5 flex items-center justify-center rounded-md bg-black/[0.06] text-[10px] font-bold text-[#999] group-hover:bg-[#4B6CF7]/10 group-hover:text-[#4B6CF7] transition-colors shrink-0 mt-0.5">
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span className="text-xs text-[#555555] group-hover:text-[#333333] leading-relaxed flex-1 transition-colors">
                            {action.label}
                          </span>
                        </div>
                        {/* 예상 결과 미리보기 */}
                        <div className="flex items-center gap-3 px-4 pb-3 pl-12">
                          <span className={`flex items-center gap-1 text-[10px] font-semibold tabular-nums ${repPos ? "text-[#4B6CF7]/70" : "text-red-400/80"}`}>
                            <i className={`${repPos ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-[9px]`}></i>
                            직급 {repVal > 0 ? "+" : ""}{repVal}pt
                          </span>
                          <span className="text-[#DDDDDD] text-[10px]">·</span>
                          <span className={`flex items-center gap-1 text-[10px] font-semibold tabular-nums ${capPos ? "text-emerald-500/70" : "text-red-400/80"}`}>
                            <i className={`${capPos ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-[9px]`}></i>
                            자본 {capVal > 0 ? "+" : ""}{Math.abs(capVal) >= 1000 ? `${(capVal / 1000).toFixed(0)}천` : capVal}만
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 팀 협의 버튼 */}
              <button
                type="button"
                onClick={() => setPhase("team_discuss")}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-[#AAAAAA]/40 text-[#888888] hover:border-[#4B6CF7]/40 hover:text-[#4B6CF7] hover:bg-[#4B6CF7]/[0.03] transition-all cursor-pointer text-xs font-medium whitespace-nowrap"
              >
                <i className="ri-team-line text-sm"></i>
                팀원들과 먼저 상의하기
              </button>
            </>
          )}

          {/* ── PHASE: team_discuss ── */}
          {phase === "team_discuss" && (
            <div>
              {!discussSent ? (
                <>
                  <p className="text-[#666666] text-xs leading-relaxed mb-4">
                    선택한 팀원들에게 <strong className="text-[#333333]">협의 요청 메일</strong>이 자동 발송됩니다. 이벤트는 <strong className="text-[#333333]">대시보드 미결 목록</strong>에 저장되어, 팀원 의견을 확인한 뒤 최종 결정할 수 있습니다.
                  </p>

                  <p className="text-[#BBBBBB] text-[10px] uppercase tracking-widest mb-2">협의할 팀원 선택</p>
                  <div className="space-y-1.5 mb-4">
                    {discussTargets.map(({ role, label, icon, reason }) => {
                      const isOn = selectedDiscussRoles.includes(role);
                      return (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setSelectedDiscussRoles(prev =>
                            isOn ? prev.filter(r => r !== role) : [...prev, role]
                          )}
                          className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                            isOn
                              ? "border-[#4B6CF7]/30 bg-[#4B6CF7]/[0.06]"
                              : "border-black/[0.07] hover:border-black/15 hover:bg-black/[0.02]"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${isOn ? "bg-[#4B6CF7]/15" : "bg-black/[0.04]"}`}>
                              <i className={`${icon} text-sm ${isOn ? "text-[#4B6CF7]" : "text-[#888888]"}`}></i>
                            </div>
                            <div>
                              <p className={`text-xs font-medium ${isOn ? "text-[#333333]" : "text-[#666666]"}`}>{label}</p>
                              <p className="text-[10px] text-[#BBBBBB]">{reason}</p>
                            </div>
                          </div>
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            isOn ? "bg-[#4B6CF7] border-[#4B6CF7]" : "border-black/20"
                          }`}>
                            {isOn && <i className="ri-check-line text-white text-[9px]"></i>}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mb-4">
                    <p className="text-[#BBBBBB] text-[10px] uppercase tracking-widest mb-1.5">추가 메모 (선택)</p>
                    <textarea
                      value={discussNote}
                      onChange={e => setDiscussNote(e.target.value)}
                      maxLength={200}
                      rows={2}
                      placeholder="팀원들에게 전달할 구체적인 질문이나 맥락을 적어주세요..."
                      className="w-full text-xs bg-[#F9F9F9] border border-black/[0.08] rounded-lg px-3 py-2.5 text-[#555555] outline-none focus:border-[#4B6CF7]/30 resize-none placeholder-[#CCCCCC]"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPhase("choice")}
                      className="flex-1 py-2.5 rounded-xl text-[#888888] border border-black/[0.08] hover:border-black/20 transition-colors cursor-pointer whitespace-nowrap text-xs"
                    >
                      돌아가기
                    </button>
                    <button
                      type="button"
                      onClick={handleSendDiscuss}
                      disabled={selectedDiscussRoles.length === 0}
                      className="flex-1 py-2.5 rounded-xl bg-[#4B6CF7] text-white hover:bg-[#5B7CF7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap text-xs font-semibold flex items-center justify-center gap-1.5"
                    >
                      <i className="ri-send-plane-line"></i>
                      협의 요청 발송
                    </button>
                  </div>
                </>
              ) : (
                <div className="py-6 flex flex-col items-center gap-3 animate-[fadeIn_0.4s_ease]">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 border border-emerald-200">
                    <i className="ri-send-plane-fill text-emerald-500 text-xl"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-[#222222] font-semibold text-sm mb-1">협의 요청 발송 완료!</p>
                    <p className="text-[#888888] text-xs leading-relaxed">
                      선택한 팀원 인박스로 메일이 전달됐어요.<br/>
                      의견을 확인한 후 대시보드 미결 목록에서<br/>최종 결정을 내려주세요.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PHASE: processing ── */}
          {phase === "processing" && selectedIdx !== null && (
            <div className="py-4">
              <div className="flex gap-3 mb-5 p-3 bg-[#4B6CF7]/[0.05] border border-[#4B6CF7]/15 rounded-xl">
                <i className="ri-check-line text-[#4B6CF7] text-base mt-0.5 shrink-0"></i>
                <div>
                  <p className="text-[#333333] text-xs font-medium mb-0.5">선택하신 대응 방식</p>
                  <p className="text-[#666666] text-xs leading-relaxed">{event.actions[selectedIdx].label}</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3 py-6">
                <div className="w-5 h-5 border-2 border-[#4B6CF7]/30 border-t-[#4B6CF7] rounded-full animate-spin"></div>
                <span className="text-[#888888] text-sm">결과 산출 중...</span>
              </div>
            </div>
          )}

          {/* ── PHASE: result ── */}
          {phase === "result" && selectedIdx !== null && selectedAction && (
            <div className="py-2 animate-[fadeIn_0.4s_ease]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 border border-emerald-200 shrink-0">
                  <i className="ri-check-double-line text-emerald-500 text-sm"></i>
                </div>
                <div>
                  <div className="text-[#111111] font-semibold text-sm">대응 완료</div>
                  <div className="text-[#BBBBBB] text-[10px]">경영 지표에 반영됩니다</div>
                </div>
              </div>

              <div className="flex gap-2.5 mb-4 p-3 bg-[#F9F9F9] border border-black/[0.07] rounded-xl">
                <i className="ri-cursor-line text-[#BBBBBB] text-sm mt-0.5 shrink-0"></i>
                <p className="text-[#555555] text-xs leading-relaxed">{selectedAction.label}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`rounded-xl border p-3.5 text-center ${repDelta >= 0 ? "bg-[#4B6CF7]/[0.06] border-[#4B6CF7]/15" : "bg-red-50 border-red-100"}`}>
                  <div className="text-[#BBBBBB] text-[9px] uppercase tracking-widest mb-1">직급 점수 변화</div>
                  <div className={`text-2xl font-bold tabular-nums ${repDelta >= 0 ? "text-[#4B6CF7]" : "text-red-500"}`}>
                    {repDelta > 0 ? "+" : ""}{repDelta}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${repDelta >= 0 ? "text-[#4B6CF7]/60" : "text-red-400"}`}>pt</div>
                </div>
                <div className={`rounded-xl border p-3.5 text-center ${capDelta >= 0 ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"}`}>
                  <div className="text-[#BBBBBB] text-[9px] uppercase tracking-widest mb-1">자본금 변화</div>
                  <div className={`text-2xl font-bold tabular-nums ${capDelta >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                    {capDelta > 0 ? "+" : ""}{capDelta >= 1000 ? `${(capDelta / 1000).toFixed(0)}천` : capDelta}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${capDelta >= 0 ? "text-emerald-500/60" : "text-red-400"}`}>만원</div>
                </div>
              </div>

              {onAddTodos && selectedIdx !== null && EVENT_TODOS[event.id]?.[selectedIdx] && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2.5 rounded-xl bg-[#4B6CF7]/[0.06] border border-[#4B6CF7]/15 animate-[fadeIn_0.5s_ease_0.4s_both]">
                  <i className="ri-checkbox-multiple-line text-[#7A9BFF] text-sm shrink-0"></i>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#4B6CF7] text-[11px] font-medium">
                      할 일 {EVENT_TODOS[event.id][selectedIdx].length}건이 오늘의 할 일에 추가되었습니다
                    </p>
                    <p className="text-[#AAAAAA] text-[10px] truncate mt-0.5">
                      {EVENT_TODOS[event.id][selectedIdx][0]?.text}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-[#4B6CF7]/50 text-xs shrink-0"></i>
                </div>
              )}

              {onOpenNPC && (
                <button
                  type="button"
                  onClick={handleOpenNPC}
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 mb-3 rounded-xl border transition-all cursor-pointer whitespace-nowrap group"
                  style={{ borderColor: npcInfo.color + "30", backgroundColor: npcInfo.color + "08" }}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-full text-white text-[10px] font-bold shrink-0"
                      style={{ backgroundColor: npcInfo.color }}
                    >
                      {npcInfo.name[0]}
                    </div>
                    <div className="text-left">
                      <div className="text-[11px] font-semibold" style={{ color: npcInfo.color }}>{npcInfo.name}</div>
                      <div className="text-[10px] text-[#AAAAAA]">{npcInfo.tag} 대화 시작 →</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full border font-semibold" style={{ color: npcInfo.color, borderColor: npcInfo.color + "40", backgroundColor: npcInfo.color + "12" }}>
                      AI NPC
                    </span>
                    <i className="ri-arrow-right-line text-xs" style={{ color: npcInfo.color + "80" }}></i>
                  </div>
                </button>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#BBBBBB]">{paused ? "마우스를 올려 일시정지 중" : "자동으로 닫힙니다"}</span>
                  <span className="text-[#BBBBBB] tabular-nums">{countdown.toFixed(1)}s</span>
                </div>
                <div className="w-full bg-black/[0.06] rounded-full h-1">
                  <div
                    className="h-1 rounded-full bg-[#4B6CF7]/50 transition-all duration-100"
                    style={{ width: `${(countdown / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
